import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Label, DENSITIES, STATUSES, TONES } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';

import { YieldLayer } from 'store/types/types';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setSelectedLayers } from 'manual';
import {
  getCropNominalWeightBySelectedLayerGroup,
  getSelectedLayers,
  getYieldLayersBySelectedCrop
} from 'manual/selectors';
import { getActiveFieldCountryCode } from 'current/selectors';
import { LAYERS_TYPES, MAX_SELECTED_LAYERS } from 'manual/constants';
import { LATAM_COUNTRIES } from 'constants/countryCodes';
import { SEASONS_ACRONYMS } from 'constants/seasons';
import { convertForLocale } from 'util/unitConversion';

interface YieldLayerItemProps {
  isDisabled: boolean;
  isSelected: boolean;
  yieldLayer: YieldLayer;
  selectYieldLayer: (yieldLayer?: YieldLayer) => void;
  showSeason: boolean;
  cropNominalWeight?: {
    q: number;
  };
}

const YieldLayerItem = memo(function Item({
  isDisabled,
  isSelected,
  yieldLayer,
  selectYieldLayer,
  showSeason,
  cropNominalWeight,
}: YieldLayerItemProps) {
  const isUnusable = !yieldLayer.isUsable;
  const isLayerDisabled = isUnusable || isDisabled;
  const onClick = isLayerDisabled ? undefined : () => selectYieldLayer(yieldLayer);
  const layerLabelKey = 'label-text__generate-zones-unusable-layer';

  const label = `${yieldLayer.year}${showSeason
    ? ' - ' + getMessage('season__' + SEASONS_ACRONYMS[yieldLayer.season])
    : ''}`;

  const averageYield = useMemo(() => {
    const value = yieldLayer?.averageYield?.v;
    const unit = yieldLayer?.averageYield?.u;

    if (!yieldLayer?.averageYield || (!value && value !== 0)) return undefined;

    try {
      return convertForLocale({
        quantity: value,
        unit: unit,
        unitType: 'area-density.yield',
        cropNominalWeight: cropNominalWeight?.q,
      }).toString();
    } catch {
      return undefined;
    }
  }, [yieldLayer, cropNominalWeight]);

  const liClassString = classNames({
    isSelected,
    isDisabled: isLayerDisabled
  });

  return (
    <li
      key={yieldLayer.id}
      className={liClassString}
      data-test-id={`yield-layer-item-${yieldLayer.id}`}
    >
      <Checkbox
        onChange={onClick}
        checked={isSelected}
        disabled={isLayerDisabled}
        label={label}
      />
      {isUnusable && (
        <Label
          density={DENSITIES.NORMAL}
          status={STATUSES.DEFAULT}
          tone={TONES.LIGHT}
          className="layer-label"
        >
          {getMessage(layerLabelKey)}
        </Label>
      )}
      {!!averageYield && (
        <span className="layer-average-yield" data-test-id="layer-average-yield">
          {averageYield}
        </span>
      )}
    </li>
  );
});

interface ZoneGenerationYieldLayerSelectionProps {
  selectedMapType: typeof LAYERS_TYPES[keyof typeof LAYERS_TYPES];
}

const ZoneGenerationYieldLayerSelection = ({ selectedMapType }: ZoneGenerationYieldLayerSelectionProps) => {
  const dispatch = useAppDispatch();
  const yieldLayers = useAppSelector(getYieldLayersBySelectedCrop);
  const selectedLayers = useAppSelector(getSelectedLayers) || [];
  const fieldCountryCode = useAppSelector(getActiveFieldCountryCode);
  const cropNominalWeight = useAppSelector(getCropNominalWeightBySelectedLayerGroup);

  const selectYieldLayer = useCallback((yieldLayer?: YieldLayer) => {
    dispatch(setSelectedLayers(yieldLayer));
  }, [dispatch]);

  if (!yieldLayers.length) return null;

  return (
    <>
      <Button
        mode="link"
        data-test-id="deselect-all-layers"
        disabled={!selectedLayers.length}
        onClick={() => selectYieldLayer()}
      >
        {getMessage('button-text__generate-zones-layers-deselect-all', {
          selectedLayers: selectedLayers.length.toString()
        })}
      </Button>
      <section data-test-id="yield-layers-header">
        {getMessage('label-text__generate-zones-layers-select-yield')}
      </section>
      <ol data-test-id="yield-layer-group-list">
        {yieldLayers.map((yieldLayer: YieldLayer) => {
          const isSelected = selectedLayers.some(({ id }) => id === yieldLayer.id);
          const isDisabled = (selectedLayers?.length === MAX_SELECTED_LAYERS[selectedMapType] && !isSelected);
          const showSeason = fieldCountryCode === LATAM_COUNTRIES.BRAZIL;

          return (
            <YieldLayerItem
              key={yieldLayer.id}
              yieldLayer={yieldLayer}
              isDisabled={isDisabled}
              isSelected={isSelected}
              selectYieldLayer={selectYieldLayer}
              showSeason={showSeason}
              cropNominalWeight={cropNominalWeight}
            />
          );
        })}
      </ol>
    </>
  );
};

export default ZoneGenerationYieldLayerSelection;
